/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { Box, Grid, Typography, Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { lockIcon } from '../../../../components/common';
import { useModule } from '../../../../../../utils/useModule';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import AppLoader from '../../../../../../components/AppLoader';
import { breadCrumbsActions } from '../../../../../../redux/slices/breadcrumbs/breadcrumbsSlice';
import ROLES from '../../../../../../utils/roles';
import { getUserDetails } from '../../../../../../utils/orgName';
import useResponsiveScreenSwitch from '../../../../../../utils/useResponsiveScreenSwitch';
import useGroupSettings from '../../../../../../hooks/useGroupSettigs';
import useOrgSettings from '../../../../../../hooks/useOrgSettings';
import { groupLoaderActions } from '../../../../../../redux/slices/groupLoaderSlice';
import useGroupDetails from '../../../../../../queries/useGroupDetails';
import useGroupList from '../../../../../../queries/useGroupList';
import ProgramTableLoader from '../../../../../../Loader/ProgramTableLoader';
import MySkeleton from '../../../../../../components/Skeleton';
import Logo from '../../../../../../assets/images/brand/dosen/logo.png';

interface TabWithLockIconProps {
  tabName: string;
  isActive: boolean;
  onClick: () => void;
  tabDisable?: boolean;
  lockTooltip?: string;
}

/* ===========================================================
   HEADER TITLE DINÂMICO PELO PATH
   =========================================================== */
function useDynamicHeaderTitle() {
  const location = useLocation();

  return useMemo(() => {
    if (location.pathname.includes('/overview')) return 'ELLIOT';
    if (location.pathname.includes('/users')) return 'Members';
    if (location.pathname.includes('/settings')) return 'PROGRAM MANAGEMENT';
    if (location.pathname.includes('/insights')) return 'Insights';
    if (location.pathname.includes('/matches')) return 'Matches';
    return 'Elliott';
  }, [location.pathname]);
}

/* ===========================================================
   DRAWER LATERAL — IGUAL AO FIGMA
   =========================================================== */
function ProgramSideDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: '#0F0F0F',
          borderRight: '1px solid #2A2A2A',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* Drawer Header */}
      <Box className="flex items-center justify-between w-full pr-2">
        <Box className="p-3 flex items-center">
          <Box className="w-9 h-9 flex items-center justify-center relative">
            <Box
              sx={{
                width: 18,
                height: 18,
                position: 'absolute',
                outline: '2px solid #FDFDFD',
                outlineOffset: '-1px',
                left: 'calc(50% - 9px)',
                top: 'calc(50% - 9px)'
              }}
            />
          </Box>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: '#868686' }} />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box className="flex flex-col gap-4 mt-4 overflow-y-auto pr-2" sx={{ flexGrow: 1 }}>
        {['Programs Dashboard', 'Insights', 'Members', 'Calendar', 'Settings'].map((item) => (
          <Box key={item} className="h-11 px-3 rounded flex items-center gap-4 cursor-pointer hover:bg-[#1A1A1A]">
            <span className="text-[#FDFDFD] text-[16px] font-normal uppercase">{item}</span>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

/* ===========================================================
   HEADER PRINCIPAL (FIGMA)
   =========================================================== */
function ProgramHeader() {
  const title = useDynamicHeaderTitle();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          height: 86,
          borderBottom: '1px solid #2A2A2A',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: '#0F0F0F'
        }}
      >
        <IconButton onClick={() => setOpen(true)}>
          <MenuIcon sx={{ color: '#FDFDFD' }} />
        </IconButton>

        <Box className="flex-1 flex items-center justify-center gap-2">
          <span className="text-white text-[20px] uppercase font-normal leading-[22px]">{title}</span>
        </Box>

        <img src={Logo} alt="Dosen Logo" style={{ width: 92, height: 18, objectFit: 'contain' }} />
      </Box>

      <ProgramSideDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ===========================================================
   WRAPPER — FUNDO PRETO + SCROLL INTERNO
   =========================================================== */
function ProgramDetailsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#0F0F0F',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <ProgramHeader />

      {/* Conteúdo com scroll */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}

/* ===========================================================
   FILE ORIGINAL (ProgramDetails) AGORA COM WRAPPER NOVO
   =========================================================== */
const ProgramDetails = () => {
  const orgGroupData = useAppSelector((state) => state.orgLevelGroup);
  const orgDetails = useAppSelector((state) => state.orgDetails);
  const allGroupData = useAppSelector((state) => state.allGroups);
  const mainGroup = useAppSelector((state) => state.allGroups.data?.mainGroup);
  const breadcrumbs = useAppSelector((state) => state.breadCrumbs.breadcrumbs);
  const isGoal = useAppSelector((state) => state.groupDetails.data?.goalPublished);
  const { isMasterclass, isMentorship } = useModule();
  const isGroupUnlocked = isMentorship ? true : isGoal;

  const dispatch = useAppDispatch();
  const { forMobile } = useResponsiveScreenSwitch();
  const grpId = useParams().id || '';
  const orgId = useParams().orgId || '';
  const coId = useParams().coId || '';
  const [searchParams] = useSearchParams();
  const role = localStorage.getItem('role');
  let orgName = searchParams.get('org') || '';
  const groupLoader = useAppSelector((state) => state.groupLoader.loading);
  const [groupType, setGroupType] = useState('');

  const currentPath = useLocation();
  let { location } = getUserDetails();
  let param = role === ROLES.platAdmin ? `/?org=${orgName}` : '';

  const groupDetails = useAppSelector((state) => state.groupDetails);
  const groupRoleList = useAppSelector((state) => state.groupRoleList);
  const groupSettings = useGroupSettings();

  const { refetch } = useGroupDetails(grpId, location);
  const { refetch: fetchGroups } = useGroupList({ orgId: coId, grpId, location });

  useEffect(() => {
    if (allGroupData.data) {
      if (mainGroup) {
        const mainGroup = breadcrumbs.find((each) => each.name === 'Program');
        dispatch(breadCrumbsActions.removeBreadCrumbs(mainGroup));
      } else {
        dispatch(
          breadCrumbsActions.updateBreadCrumbs({
            id: grpId,
            name: allGroupData.data.mainGroupName,
            url: `/app/programdetails/${orgId}/${coId}/${grpId}/overview${param}`
          })
        );
      }
    }
  }, [mainGroup, allGroupData.data]);

  useEffect(() => {
    if (groupDetails?.data) {
      setGroupType(groupDetails?.data?.type);
    }
  }, [groupDetails?.data]);

  useEffect(() => {
    const newGrpId = grpId === 'admin' ? '' : grpId;
    fetchGroups();
    if (newGrpId) {
      refetch();
    }
    dispatch(groupLoaderActions.updateState(false));
  }, [grpId]);

  const activeTab = window.location.href
    .split('/')
    .find((each) => ['users', 'settings', 'insights', 'matches', 'overview'].includes(each));
  const url = `/app/programdetails/${orgId}/${coId}/${grpId}`;

  const navigate = useNavigate();
  const orgSettings = useOrgSettings();
  // const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);

  /** Loader */
  const programLoader =
    orgGroupData.loading ||
    allGroupData?.loading ||
    groupRoleList.loading ||
    orgDetails.loading ||
    groupDetails.loading;

  const skeletonLoader = () =>
    Array.from({ length: 4 }).map((_, index) => (
      <React.Fragment key={index}>
        <Grid item xs={12} md={6} lg={12 / 4}>
          <MySkeleton variant="rounded" height={245} />
        </Grid>
      </React.Fragment>
    ));

  const shouldRenderAppLoader = programLoader && groupType !== 'level';
  if (shouldRenderAppLoader) {
    return <AppLoader />;
  }
  if (groupLoader) {
    return null;
  }

  if (orgGroupData.error) {
    return <Box>{orgGroupData.errorText}</Box>;
  }

  if (orgDetails.error) {
    return <Box>{orgDetails.errorText}</Box>;
  }

  /** Tabs design with all requrments */
  function createTabWithLockIcon({ tabName, isActive, onClick, tabDisable, lockTooltip }: TabWithLockIconProps) {
    const TabDisableValue = groupType === 'level' ? (tabName !== 'Overview' ? true : false) : tabDisable;

    return (
      <Box
        pb={1}
        gap="3px"
        display="flex"
        alignItems="center"
        onClick={TabDisableValue ? undefined : onClick}
        borderBottom={isActive ? '3px solid #DF6438' : undefined}
        className={`cursor-${TabDisableValue ? 'not-allowed' : 'pointer'}`}
      >
        {TabDisableValue && lockIcon({ title: lockTooltip })}

        <Typography
          lineHeight="normal"
          fontWeight={isActive ? '700' : '400'}
          color={isActive ? '#DF6438' : 'primary'}
          className={TabDisableValue ? 'disabled-box-opacity-25' : ''}
        >
          {tabName}
        </Typography>
      </Box>
    );
  }

  const getTabs = () => {
    if (!mainGroup) {
      return (
        <Box borderBottom="1px solid #EFF0F4" mb={2}>
          <Box display="flex" width="100%" overflow="auto">
            {breadcrumbs.map((each, index) => (
              <Fragment key={index}>
                {index === breadcrumbs.length - 1 ? (
                  <Box>
                    <Typography variant="caption" noWrap>
                      {each.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Link to={each.url} onClick={() => dispatch(groupLoaderActions.updateState(true))}>
                      <Typography noWrap variant="caption" fontWeight="400" color="secondary">
                        {each.name}
                      </Typography>
                    </Link>
                    <Typography variant="caption" fontWeight="400" mx={0.5} color="secondary">
                      /
                    </Typography>
                  </Box>
                )}
              </Fragment>
            ))}
          </Box>

          {/* Group header with tabs */}
          <Box
            display={forMobile ? 'block' : 'flex'}
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            mt={1}
          >
            <Typography variant="h3" noWrap mb={1} mt={'8px'} lineHeight={'1.3'}>
              {_.get(allGroupData, 'data.mainGroupName', '')}
            </Typography>

            <Box display="flex" columnGap="40px">
              {/* Overview */}
              {createTabWithLockIcon({
                tabName: 'Overview',
                isActive: activeTab === 'overview',
                onClick: () => navigate(`${url}/overview${param}`),
                tabDisable: !(groupSettings && isGroupUnlocked)
              })}
              {/* Insights */}
                  {groupDetails.data?.type !== 'level' &&
                    createTabWithLockIcon({
                      tabName: 'Insights',
                      isActive: activeTab === 'insights',
                      onClick: () => navigate(`/app/programdetails/${orgId}/${coId}/admin/insights${param}`),
                      tabDisable: !(groupSettings && isGroupUnlocked)
                    })}
              {groupDetails.data?.type !== 'level' &&
                createTabWithLockIcon({
                  tabName: 'Members',
                  isActive: activeTab === 'users',
                  onClick: () => navigate(`${url}/users${param}`),
                  tabDisable: false
                })}
              {/* Matches */}
              {groupDetails.data?.type !== 'level' &&
                !forMobile &&
                createTabWithLockIcon({
                  tabName: 'Matches',
                  isActive: activeTab === 'matches',
                  onClick: () => navigate(`${url}/matches${param}`),
                  tabDisable: isMasterclass || !(groupSettings && isGroupUnlocked), // Disable for Masterclass always
                  lockTooltip: isMasterclass ? 'This is not needed for the Masterclass model' : '' // Tooltip for Masterclass
                })}
              {/* Settings */}
              {groupDetails.data?.type !== 'level' &&
                !forMobile &&
                createTabWithLockIcon({
                  tabName: 'Settings',
                  isActive: activeTab === 'settings',
                  onClick: () => navigate(`${url}/settings${param}`)
                })}
            </Box>
          </Box>
        </Box>
      );
    }
    return null;
  };
  const checkSettings = () => {
    if (groupType === 'level') {
      return <Outlet />;
    }

    if (
      !mainGroup &&
      (!groupSettings || !isGroupUnlocked) &&
      !currentPath.pathname.includes('/settings') &&
      orgSettings &&
      !currentPath.pathname.includes('/users')
    ) {
      return <Navigate to={`/app/programdetails/${orgId}/${coId}/${grpId}/settings`} />;
    }
    return <Outlet />;
  };

  if (orgGroupData) {
    if (allGroupData.error) {
      return <Box>{allGroupData.errorText}</Box>;
    }
    if (allGroupData.data && orgDetails.data) {
      return checkSettings();
    }
  }

  return null;
};

export default ProgramDetails;
